import {
  Overrides,
  ParsedEnglishMetaField,
  ParsedFrenchMetaField,
} from '@context/SaveForLaterContext/types';
import { Category, Maybe, MetafieldEdge } from '../schema';
import {
  TagColour,
  Hierarchy,
  MecColour,
  MecImage,
  Tags,
  TechSpec,
  LocalizedFields,
  MecVariant,
  ProductImageType,
  ProductTagFromFile,
  isPrimaryProductImage,
} from '@adapters/bigcommerce/types/product';
import { isLfsProductScopedSwatch } from '@utils/lfsProductScope';
import { BigCommerceLocaleString } from '../types/locale';
import tagsEN from '../../../product-tags-en.json';
import tagsFR from '../../../product-tags-fr.json';

export const metaFieldReducer = <MetaFieldCollection>(
  acc: MetaFieldCollection | Record<string, unknown>,
  metaField?: MetafieldEdge | null
): MetaFieldCollection | Record<string, unknown> => {
  if (!metaField?.node) return acc;
  // One malformed metafield used to throw and take down the whole PDP or cart.
  try {
    return {
      ...acc,
      [metaField.node.key]: JSON.parse(metaField.node.value),
    };
  } catch {
    console.error(`Invalid JSON in metafield "${metaField.node.key}"`);
    return acc;
  }
};

export const alphabet = () => {
  const alpha = Array.from(Array(26)).map((e, i) => i + 97);
  return alpha.map((x) => String.fromCharCode(x));
};

export const sortProductColours = (
  productColours: MecColour[],
  queryColour: string | string[] | undefined
) => {
  if (!productColours || !queryColour || typeof queryColour !== 'string') {
    return productColours;
  } else {
    const normalizedQueryColour = queryColour.trim();
    const sortedProductColours = [...productColours].sort((a, b) => {
      if (a.name === normalizedQueryColour) {
        return -1;
      }
      if (b.name === normalizedQueryColour) {
        return 1;
      }
      return 0;
    });
    return sortedProductColours;
  }
};

const moveInitialImageToFront = (
  productImages: MecImage[],
  indexOfInitialImage: number
) => {
  const removeInitialImage = productImages.splice(indexOfInitialImage, 1);
  return [...removeInitialImage, ...productImages];
};

export const sortProductImages = (
  productImages: MecImage[],
  queryColour: string | string[] | undefined
) => {
  if (!productImages || !queryColour || typeof queryColour !== 'string') {
    return productImages;
  } else {
    const decodedQueryColour = queryColour?.replaceAll(/%2F/g, '/').trim();

    /** Tier 0 = matches selected colour; tier 1 = product-scoped LFS (all variants); tier 2 = rest. */
    const selectionTier = (img: MecImage): number => {
      if (img.swatchMatch === decodedQueryColour) return 0;
      if (
        img.imageType === ProductImageType.LFS &&
        isLfsProductScopedSwatch(img.swatchMatch)
      ) {
        return 1;
      }
      return 2;
    };

    const sortedProductImages = [...productImages].sort(
      (a, b) => selectionTier(a) - selectionTier(b)
    );
    const indexOfInitialImage = sortedProductImages.findIndex(
      (image) =>
        image.swatchMatch === decodedQueryColour && isPrimaryProductImage(image)
    );

    const sortedProductImagesWithInitialImageAtFront =
      indexOfInitialImage !== -1
        ? moveInitialImageToFront(sortedProductImages, indexOfInitialImage)
        : sortedProductImages;

    return sortedProductImagesWithInitialImageAtFront;
  }
};

export const transformOverrides = (
  english: ParsedEnglishMetaField,
  french: ParsedFrenchMetaField,
  optionValues: { colour?: string; size?: string; valueId: number }[]
) => {
  // Note: our French overrides contain both French and English data for colour and size.
  const colour = optionValues?.find((item) => {
    return item.colour;
  });

  const size = optionValues?.find((item) => {
    return item.size;
  });

  const coloursDict =
    french.colour?.find((item) => {
      if (item?.label_en === colour?.colour) {
        return {
          en: item.label_en,
          fr: item.label_fr,
        };
      }
    }) ?? null;

  const sizes =
    french.size?.find(({ label_en, label_fr }) => {
      if (label_en === size?.size) {
        return {
          en: label_en,
          fr: label_fr,
        };
      }
    }) ?? null;

  const transformedOutput: Overrides = {
    name: {
      en: english.name,
      fr: french.name,
    },
    slug: {
      en: english.slug,
      fr: french.slug,
    },
    url: {
      en: english.url,
      fr: french.url,
    },
    url_encoded: {
      en: english.url_encoded,
      fr: french.url_encoded,
    },
    web_name: {
      en: english.web_name,
      fr: french.web_name,
    },
    colour: {
      en: coloursDict?.label_en ?? '',
      fr: coloursDict?.label_fr ?? '',
    },
    size: { en: sizes?.label_en ?? '', fr: sizes?.label_fr ?? '' },
  };

  return transformedOutput;
};

interface ProductTags {
  edges?:
    | Maybe<
        Maybe<
          {
            __typename?: 'CategoryEdge' | undefined;
          } & {
            node: {
              __typename?: 'Category' | undefined;
            } & Pick<
              Category,
              'name' | 'path' | 'entityId' | 'breadcrumbs' | 'seo'
            >;
          }
        >[]
      >
    | undefined;
}

type TagsObjectType = { [key: string]: ProductTagFromFile };

export const tagsFromFile = (locale: string): TagsObjectType => {
  return locale === 'en'
    ? (tagsEN as TagsObjectType)
    : (tagsFR as TagsObjectType);
};

const getTagColour = (metaKeywords: string[]) => {
  const primaryPrice = ['primary', 'price'];
  const secondryPrice = ['secondary', 'price'];
  let tagColour = null;

  primaryPrice.every((item) => metaKeywords.includes(item));
  if (primaryPrice.every((item) => metaKeywords.includes(item))) {
    tagColour = TagColour.red;
  } else {
    tagColour = TagColour.black;
  }

  if (secondryPrice.every((item) => metaKeywords.includes(item))) {
    tagColour = TagColour.redOutline;
  }
  return tagColour;
};

export const sortProductTags = (
  productTags: ProductTags['edges'],
  variants: MecVariant[] | { sku: string }[],
  locale: string
) => {
  if (!productTags || productTags.length === 0) {
    return [];
  }

  const tags = productTags?.filter((tag) => {
    if (locale === 'fr') {
      return tag?.node?.seo?.pageTitle === Tags.tags_fr;
    } else {
      return tag?.node?.seo?.pageTitle === Tags.tags;
    }
  });

  const transformTags = tags?.map((tag) => {
    if (tag?.node?.name && tag?.node?.seo?.metaKeywords) {
      const metaKeywordsArray = tag?.node?.seo?.metaKeywords.split(' ');
      const colourOverride = getTagColour(metaKeywordsArray);

      const metaDescriptionArray = tag?.node?.seo?.metaDescription.split(' ');

      const variantsWithTags = metaDescriptionArray.filter(
        (item: string | null): item is string =>
          item !== null && variants.some((obj) => obj.sku === item)
      );

      const isLink = metaKeywordsArray.includes('price') ? false : true;

      const getUniqueColourIds = (
        variantsWithTagsArray: string[],
        variantsArray: any[]
      ) => {
        // Step 1: Filter the variantsArray where sku exists in variantsWithTagsArray
        const filteredData = variantsArray.filter((item) => {
          return item.sku !== null && variantsWithTagsArray.includes(item.sku);
        });

        // Type guard to check if item is MecVariant
        const isMecVariant = (
          item: MecVariant | { sku: string }
        ): item is MecVariant => {
          return (item as MecVariant).colourId !== undefined;
        };

        // Step 2: Count occurrences of each colourId in the original variantsArray
        const originalColourCounts = variantsArray.reduce((acc, item) => {
          if (
            isMecVariant(item) &&
            item.colourId !== null &&
            item.colourId !== undefined &&
            item.availableForSaleOnline
          ) {
            acc[item.colourId] = (acc[item.colourId] || 0) + 1;
          }
          return acc;
        }, {} as Record<number, number>);

        // Step 3: Count occurrences of each colourId in the filteredData
        const filteredColourCounts = filteredData.reduce((acc, item) => {
          if (
            isMecVariant(item) &&
            item.colourId !== null &&
            item.colourId !== undefined &&
            item.availableForSaleOnline
          ) {
            acc[item.colourId] = (acc[item.colourId] || 0) + 1;
          }
          return acc;
        }, {} as Record<number, number>);

        // Step 4: Only keep colourIds that have the same count in filteredData as in original variantsArray
        const uniqueColourIds = Object.keys(filteredColourCounts)
          .filter((colourId) => {
            return (
              filteredColourCounts[Number(colourId)] ===
              originalColourCounts[Number(colourId)]
            );
          })
          .map(Number); // Convert the keys (which are strings) back to numbers

        return uniqueColourIds;
      };

      const uniqueColourIds = getUniqueColourIds(variantsWithTags, variants);

      return {
        name: tag?.node?.name,
        path: tag?.node?.path,
        id: tag?.node.entityId,
        hierarchy: metaKeywordsArray.includes(Hierarchy.primary)
          ? Hierarchy.primary
          : Hierarchy.secondary,
        colourOverride,
        variants: variantsWithTags ?? [],
        isLink,
        colours: uniqueColourIds,
      };
    }
    return null;
  });

  const sortedTags = transformTags?.sort((a, b) => {
    if (a?.hierarchy === Hierarchy.primary) {
      return -1;
    }
    if (b?.hierarchy === Hierarchy.secondary) {
      return 1;
    }
    return 0;
  });

  return sortedTags;
};

export const techSpecOverrides = (
  techSpecs: TechSpec[],
  language: BigCommerceLocaleString
): TechSpec[] => {
  const replaceText = (text: string): string => {
    if (language === 'en') {
      return text.replace(
        'Certified Responsible Down',
        '<a href="/en/explore/animal-welfare">Certified Responsible Down</a>'
      );
    } else if (language === 'fr') {
      return text.replace(
        'certifié duvet éthique',
        '<a href="/fr/explore/animal-welfare">certifié duvet éthique</a>'
      );
    }
    return text;
  };

  return techSpecs.map((spec) => {
    if (
      spec.key === 'Product sustainability' ||
      spec.key === 'Caractéristique écologique'
    ) {
      if (Array.isArray(spec.value)) {
        return {
          ...spec,
          value: spec.value.map((item) => replaceText(item)),
        };
      } else {
        return {
          ...spec,
          value: replaceText(spec.value),
        };
      }
    }
    return spec;
  });
};

export const encodeGiftCardSlug = (overrides: LocalizedFields['overrides']) => {
  if (!overrides?.url) return '';
  const encodedPart =
    (overrides?.slug && encodeURIComponent(overrides.slug)) ?? '';
  const giftcardAltEncodedUrl = overrides?.url.replace(
    overrides.slug,
    encodedPart
  );
  return giftcardAltEncodedUrl;
};
